import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

// Calm, infra-grade button variants. Periwinkle for primary CTA;
// outline + ghost for secondary actions. NO gradient, NO oversized
// shadow. Font weight stays at 500 max (per design discipline).
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ocean-400 focus-visible:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "bg-ocean-600 text-white hover:bg-ocean-800",
        outline:
          "border border-ocean-200 bg-white text-ocean-800 hover:bg-ocean-50",
        ghost:
          "bg-transparent text-ocean-800 hover:bg-ocean-50",
        danger:
          "bg-danger-600 text-white hover:bg-danger-600/90",
        subtle:
          "bg-ocean-100 text-ocean-800 hover:bg-ocean-200",
      },
      size: {
        sm: "h-8 px-3 text-xs",
        md: "h-10 px-4",
        lg: "h-11 px-5 text-base",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
